import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from "@angular/core";
import { SharedModule } from '@Shared/shared.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ConfirmationDialogService, RoutesEnum, TeamsService, UserAdminService, RoutingService, NotificationSeverity, NotificationService } from "@Services";
import { SubSink } from "subsink";
import { UserSelectionTableComponent } from "../users-selection-table/users-selection-table.component";
import { MrsColor } from "@Enums";
import { TeamUserRole } from "@Enums";
import { GetManageTeamsResponse } from "@App/manage-users/models/get-manage-teams-response.interface";
import moment from 'moment';
import editTeamRequest from "@Models/global/edit-team-request";

@Component({
    selector: 'edit-team',
    host: { 'class': 'content' },
    templateUrl: './edit-team.component.html',
    styleUrls: ['./edit-team.component.scss'],
    imports: [SharedModule, UserSelectionTableComponent]
})
export class EditTeamComponent implements OnInit, OnDestroy {
    selectedTabIndex: number = 0;

    @Input() teams: any[];

    adminUserList: any[] = [];
    teamMemberList: any[] = [];
    selectedAdminUsers: any[] = [];
    selectedTeamMembers: any[] = [];

    editTeamForm: UntypedFormGroup;
    teamData: any = null;
    allteams: GetManageTeamsResponse[] = [];
    private readonly subs = new SubSink();

    constructor(
        private _fb: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<EditTeamComponent>,
        private teamsService: TeamsService,
        private confirmationService: ConfirmationDialogService,
        private userAdminService: UserAdminService,
        private routingService: RoutingService,
        private notificationService: NotificationService
    ) {
        this.teamData = data.teams;
    }

    ngOnInit(): void {
        this.editTeamForm = this._fb.group({
            teamName: [this.teamData?.teamName, [
                Validators.required,
                Validators.maxLength(50),
                this.noWhitespaceValidator,
                this.duplicateTeamNameValidator.bind(this)
            ]],
            clientId: [this.teamData?.clientId],
            adminUsers: [[], this.atLeastOneSelectedValidator],
            teamMembers: [[], this.atLeastOneSelectedValidator]
        });
        this.getAllTeams();
        this.getEligibleUsers();
    }

    getEligibleUsers() {
        this.subs.add(this.teamsService.getAdminUsersForClient(this.teamData.clientId).subscribe(result => {
            if (result) {
                this.adminUserList = result;
                this.selectedAdminUsers = this.adminUserList.filter(u =>
                    this.teamData?.teamUsers?.some(tu => tu.id === u.id &&
                        (tu.teamRoleId === TeamUserRole.AdminOnly || tu.teamRoleId === TeamUserRole.AdminAndMember))
                );
                this.editTeamForm.get('adminUsers').setValue(this.selectedAdminUsers);
                this.editTeamForm.get('adminUsers').updateValueAndValidity();
            }
        }));
        this.subs.add(this.teamsService.getUsersForClient(this.teamData.clientId).subscribe(result => {
            if (result) {
                this.teamMemberList = result;
                this.selectedTeamMembers = this.teamMemberList.filter(u =>
                    this.teamData?.teamUsers?.some(tu => tu.id === u.id &&
                        (tu.teamRoleId === TeamUserRole.MemberOnly || tu.teamRoleId === TeamUserRole.AdminAndMember))
                );
                this.editTeamForm.get('teamMembers').setValue(this.selectedTeamMembers);
                this.editTeamForm.get('teamMembers').updateValueAndValidity();
            }
        }));
    }

    getAllTeams() {
        this.subs.add(this.userAdminService.getAllTeams(true).subscribe(data => {
            this.allteams = data.map((team: GetManageTeamsResponse) => ({
                ...team,
                id: team.id,
                name: team.name,
                activeMembers: team.activeMembers,
                activeCase: team.activeCasesCount,
                creationDate: moment(team.creationDate).utc().format('l') === '1/1/0001' ? '' : moment(team.creationDate).utc().format('l')
            }));
        }));
    }

    onAdminSelectionChange(selectedAdmins) {
        this.editTeamForm.get('adminUsers').setValue(selectedAdmins);
        this.editTeamForm.get('adminUsers').updateValueAndValidity();
    }

    onTeamMemberSelectionChange(selectedMembers) {
        this.editTeamForm.get('teamMembers').setValue(selectedMembers);
        this.editTeamForm.get('teamMembers').updateValueAndValidity();
    }

    saveChanges() {
        if (this.editTeamForm.valid) {
            const adminMembers = this.editTeamForm.get('adminUsers').value;
            const teamMembers = this.editTeamForm.get('teamMembers').value;

            const userMap = new Map<string, { userId: string, role: TeamUserRole }>();

            adminMembers.forEach(u => {
                userMap.set(u.id, { userId: u.id, role: TeamUserRole.AdminOnly });
            });

            teamMembers.forEach(u => {
                if (userMap.has(u.id)) {
                    userMap.set(u.id, { userId: u.id, role: TeamUserRole.AdminAndMember });
                } else {
                    userMap.set(u.id, { userId: u.id, role: TeamUserRole.MemberOnly });
                }
            });

            const users = Array.from(userMap.values());

            let request: editTeamRequest = {
                teamId: this.teamData.id,
                name: this.editTeamForm.get('teamName').value,
                clientId: this.teamData.clientId,
                teamMembers: users
            };

            this.subs.add(this.teamsService.editTeam(request).subscribe(result => {
                if (result) {
                    this.dialogRef.close(this.editTeamForm.value);
                }
            }));
        }
    }

    deleteTeam() {
        this.confirmationService.open({
            title: `Are you sure you want to delete this team?`,
            message: `This will permanently delete the team, but will not affect users and cases. \n
            Assigned cases will remain assigned. Unassigned cases will be redistributed across roles`,
            okLabel: 'DELETE THE TEAM',
            okTextColor: MrsColor.UIRed,
            onOk: () => {
                this.teamsService.deleteTeam(this.teamData.id).subscribe(
                    _data => {
                        this.notificationService.showNotification({
                            severity: NotificationSeverity.Success,
                            message: `${this.teamData?.teamName} has been deleted.`,
                            actionLabel: 'CLOSE'
                        });
                        this.dialogRef.close();
                        this.routingService.navigateToRoute(RoutesEnum.teams);
                    }
                );
            },
            cancelLabel: 'CANCEL',
            showCancel: true,
        });
    }

    cancel() {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    duplicateTeamNameValidator(control) {
        if (!control.value) {
            return null;
        }

        const parent = control.parent;
        if (parent) {
            const clientId = parent.get('clientId')?.value;
            if (clientId) {
                const name = control.value.trim().toLowerCase();
                const isDuplicate = this.allteams
                    .filter(team => team.clientId === clientId)
                    .some(team => team.name.toLowerCase() === name);

                return isDuplicate ? { duplicate: true } : null;
            }
        }
        return null;
    }

    noWhitespaceValidator(control) {
        const isWhitespace = (control.value || '').trim().length == 0;
        return isWhitespace ? { whiteSpace: true } : null;
    }

    atLeastOneSelectedValidator(control) {
        return Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
    }
}